/**
 * Icon <uo-icon>
 *
 * A custom web component for rendering icons, including both glyphs and integration icons.
 * Supports various properties such as size, animation, icon style, and more.
 *
 * Attributes:
 * - `id`: The ID of the icon
 * - `integration`: Specifies an integration to render an icon for
 * - `size`: Defines the size of the icon (e.g., "small", "large", "16")
 * - `animation`: Applies animation to the icon (e.g., "spin")
 * - `icon-style`: Specifies the icon style ("solid", "regular", "duotone")
 * - `hide-missing`: Hides the icon if it is not found
 * - `show-tooltip`: Displays the name of the icon in a tooltip
 */

// Import global styles
// This will load the font
import './style.scss';

// Import AI icon component
import './components/icon-ai';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uo-icon' )
export class AutomatorIcon extends LitElement {
	/**
	 * Styles for the component, imported from SCSS file.
	 * @type {CSSResultGroup}
	 */
	static styles = css`${unsafeCSS( styles )}`;

	/**
	 * The ID of the icon to render.
	 * @type {String}
	 */
	@property( { type: String } ) accessor id;

	/**
	 * The name of the integration to render the icon for.
	 * Integration icons are rendered as images instead of glyphs.
	 * @type {String}
	 */
	@property( { type: String } ) accessor integration;

	/**
	 * The size of the icon.
	 * Supported values: "xsmall", "small", "large", "xlarge", "2x", "16", "24", "32", "40", "64".
	 * @type {String}
	 */
	@property( { type: String } ) accessor size;

	/**
	 * The animation applied to the icon.
	 * Supported values: "spin".
	 * @type {String}
	 */
	@property( { type: String } ) accessor animation;

	/**
	 * The style of the icon to render.
	 * Supported values: "solid", "regular"
	 * Default value is "regular".
	 * @type {String}
	 */
	@property( { type: String, attribute: 'icon-style' } ) accessor iconStyle = 'regular';

	/**
	 * Whether the icon is duotone
	 * @type {Boolean}
	 */
	@property( { type: Boolean, attribute: 'is-duotone' } ) accessor isDuotone = false;

	/**
	 * Whether to show the name of the icon in a tooltip.
	 * Default value is `false`.
	 * @type {Boolean}
	 */
	@property( { type: Boolean, attribute: 'show-tooltip' } ) accessor showName = false;

	/**
	 * Renders the component.
	 * @return {TemplateResult} The rendered template.
	 */
	render() {
		return html`
			<div
				class="icon"
				size="${ifDefined( this.size )}"
				animation="${ifDefined( this.animation )}"
			>
				${ ! isEmpty( this.integration ) ? this.templateIntegrationIcon : this.templateGlyph }
			</div>
		`;
	}

	/**
	 * Creates the template to render a glyph.
	 *
	 * @return {TemplateResult} The template for the glyph icon.
	 */
	get templateGlyph() {
		// Check if it's a custom icon
		if ( this.customGlyphs.includes( this.id ) ) {
			return html`
				<div
					class="icon-img"
					id="${ this.id }"
					is-custom
				></div>
			`;
		}

		// Set the classes of the icon
		const iconClasses = [];

		// Add the style
		iconClasses.push( `icon-glyph-${ this.iconStyle }` );

		// Add duotone
		if ( this.isDuotone ) {
			iconClasses.push( 'icon-glyph-duotone' );
		}

		// Add the ID
		iconClasses.push( `icon-glyph-${ this.id }` );

		return html`
			<i class=${ iconClasses.join( ' ' ) }></i>
		`;
	}

	/**
	 * Creates the template to render an integration icon.
	 * Integration icons are images rather than font glyphs.
	 *
	 * @return {TemplateResult|undefined} The template for the integration icon.
	 */
	get templateIntegrationIcon() {
		// Get the integration
		const integration = UncannyAutomatorBackend?.components?.icon?.integrations?.[ this.integration ];

		if ( ! isDefined( integration ) ) {
			return;
		}

		const $integrationIcon = html`
			<div 
				class="icon-integration"
				style="background-image: url(${integration.icon})"
			></div>
		`;

		if ( ! this.showName ) {
			return $integrationIcon;
		}

		return html`
			<uo-tooltip>
				${ integration.name }

				<span slot="target">
					${ $integrationIcon }
				</span>
			</uo-tooltip>
		`;
	}

	/**
	 * List of custom icons that cannot be added to the font.
	 * These are typically multi-color icons.
	 *
	 * @return {String[]} Array of IDs for custom icons.
	 */
	get customGlyphs() {
		return [
			'google',
			'microsoft',
			'facebook',
			'x-twitter',
			'youtube',
		];
	}
}
